import React, { useState } from 'react';
import { Card, Button, Typography, Alert, theme } from 'antd';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { IconShieldCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getStripeConfig } from '../services/StripeService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Text } = Typography;

// Same module-level cache as the payment step's: one loadStripe() per tab,
// not one per mount.
let stripePromiseCache: Promise<Stripe | null> | null = null;
const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromiseCache) {
    stripePromiseCache = getStripeConfig().then((config) => loadStripe(config.publishableKey));
  }
  return stripePromiseCache;
};

/**
 * Saves a card without charging it -- the step a guest sees after accepting a
 * quote from a host who charges at handover.
 *
 * Its whole job is to make "nothing is being charged" believable while asking
 * for a card, which is not the reflex anybody has when a card form appears.
 * Hence the reassurance above the form rather than below it, and a button
 * that says what it does instead of "pagar".
 *
 * Technically this is confirmSetup, NOT confirmPayment. Getting that wrong
 * charges somebody who was promised they would not be, which is why it lives
 * in its own component rather than as a flag on the payment step.
 */
const SaveCardForm: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);

    // redirect: 'if_required' keeps the guest here for the ordinary card
    // case; a bank that insists on its own 3-D Secure page is exactly the
    // reason this step exists at all, and it is far better to walk through it
    // now, with the guest present, than to have it silently decline an
    // off-session hold days from now.
    const { error: confirmError } = await stripe.confirmSetup({ elements, redirect: 'if_required' });

    if (confirmError) {
      setError(confirmError.message || translate(t, TRANSLATIONS.SAVE_CARD_ERROR));
      setSaving(false);
      return;
    }
    onSaved();
  };

  return (
    <Card bordered={false} style={{ borderRadius: 18 }}>
      <Text strong style={{ fontSize: 16, display: 'block' }}>
        {translate(t, TRANSLATIONS.SAVE_CARD_TITLE)}
      </Text>

      {/* Before the form, not after: somebody looking at a card field has
          already started worrying about being charged. */}
      <div
        style={{
          marginTop: 12,
          padding: '12px 14px',
          borderRadius: 14,
          background: TEAL_SOFT_BG,
          border: `1px solid ${TEAL}`,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <span style={{ display: 'flex', flexShrink: 0, color: TEAL, marginTop: 1 }}>
          <IconShieldCheck size={16} />
        </span>
        <Text style={{ fontSize: 13, color: TEAL }}>{translate(t, TRANSLATIONS.SAVE_CARD_REASSURANCE)}</Text>
      </div>

      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <PaymentElement />
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16, borderRadius: 14 }} />}

      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
        {translate(t, TRANSLATIONS.SAVE_CARD_WHEN_CHARGED)}
      </Text>

      <Button
        type="primary"
        block
        loading={saving}
        onClick={handleConfirm}
        style={{ borderRadius: 999, background: TEAL, borderColor: TEAL, height: 44 }}
      >
        {translate(t, TRANSLATIONS.SAVE_CARD_CONFIRM)}
      </Button>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8, textAlign: 'center', color: token.colorTextTertiary }}>
        {translate(t, TRANSLATIONS.SAVE_CARD_NO_CHARGE_NOW)}
      </Text>
    </Card>
  );
};

/** Bootstraps Stripe.js before mounting <Elements> -- same shape as BookingPaymentStep, pointed at a SetupIntent instead. */
const SaveCardStep: React.FC<{ setupClientSecret: string; onSaved: () => void }> = ({ setupClientSecret, onSaved }) => {
  const [stripePromise] = useState(() => getStripePromise());

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret: setupClientSecret, appearance: { variables: { colorPrimary: TEAL, borderRadius: '12px' } } }}
    >
      <SaveCardForm onSaved={onSaved} />
    </Elements>
  );
};

export default SaveCardStep;
